import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateCompiler, TranslateLoader, TranslateModule, TranslateService, TranslateStore } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { KeycloakAngularModule, KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CustomTranslationCompiler } from './translation/custom-translation-compiler';
import { environment } from 'src/environments/environment';

export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function initializeKeycloak(keycloak: KeycloakService) {
    // We need to async/await the keycloak init because if we don't the AuthGuard on the routing will load and do an
    // authentication check without waiting for the outcome if the keycloak init, then authentication will be false and 
    // we will get stuck in a loop where we reload constantly as the AuthGuard sends us to login and keycloak sends
    // us back because we are already logged in
    return async () => {
            await keycloak.init({
                config: {
                    url: environment.authUrl,
                    realm: 'CDEP',
                    clientId: environment.clientId,
                },
                initOptions: {
                    onLoad: 'login-required',
                    checkLoginIframe: false,
                    // the default responseMode ('fragment') wasn't working as keycloak 
                    // would append params perpetually on repeated refreshes and append them incorrectly. 
                    // While not preferred, 'query' is the mode that will work for us.
                    responseMode: 'query',
                },
            });

            keycloak.keycloakEvents$.subscribe({
                next(event) {
                    if (event.type == KeycloakEventType.OnTokenExpired) {
                        // The updateToken method takes in the number of seconds that can be left before a token expires and still have it refresh anyway, default is 20.
                        keycloak.updateToken();
                    }
                }
            });
        }
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        AppRoutingModule,
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        KeycloakAngularModule,
        TranslateModule.forRoot({
            defaultLanguage: 'en',
            compiler: {
                provide: TranslateCompiler,
                useClass: CustomTranslationCompiler,
            },
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
        }),
    ],
    providers: [
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy,
        },
        TranslateService,
        TranslateStore,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeKeycloak,
            multi: true,
            deps: [KeycloakService],
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
